import React, { useEffect } from 'react';
import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import 'bulma/css/bulma.min.css';
import { InfoService } from '../../../services/site/InfoService';
import './LegalStyles.scss';

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

// Animated section wrapper
const AnimatedSection: React.FC<{ 
  children: React.ReactNode; 
  className?: string;
}> = ({ children, className = '' }) => {
  const controls = useAnimation();
  const [ref, inView] = useInView({ threshold: 0.1, triggerOnce: true });

  useEffect(() => {
    if (inView) {
      controls.start('visible');
    }
  }, [controls, inView]);

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={controls}
      variants={fadeInUp}
      className={className}
    >
      {children}
    </motion.div>
  );
};

// ============================================
// PRIVACY POLICY COMPONENT
// ============================================
export const CancellationPolicy: React.FC = () => {
  return (
    <div className="sds_legal-wrapper">
      {/* SEO Meta */}
      <div style={{ display: 'none' }}>
        <h1>Cancellation & Refund Policy</h1>
        <meta name="description" content="Cancellation & Refund Policy" />
      </div>

      <div className="container">
        <AnimatedSection className="sds_legal-header section">
          <div className="has-text-centered">
            <motion.div
              className="sds_legal-icon"
              animate={{ rotate: [0, 10, -10, 0] }}
              transition={{ duration: 2.5, repeat: Infinity }}
            >
              🔄
            </motion.div>
            <h1 className="title is-1 sds_legal-title">Cancellation & Refund Policy</h1>
            <p className="subtitle is-5 sds_legal-subtitle">
              Last Updated: October 17, 2025
            </p>
            <p className="sds_legal-intro">
              HDT Solutions believes in helping its customers as far as possible, and has therefore a liberal cancellation policy.
            </p>
          </div>
        </AnimatedSection>

        <motion.div variants={staggerContainer} initial="hidden" animate="visible">
          <AnimatedSection className="sds_legal-section">
            <h2 className="title is-3 sds_legal-section-title">1. Cancellation Policy</h2>
            <div className="content">
              <p>Cancellations will be considered only if the request is made within <strong>2 days of placing the order</strong>. However, the cancellation request may not be entertained if the orders have been communicated to the vendors/merchants and they have initiated the process of shipping them.</p>
              <div className="notification is-info is-light mt-4">
                <p><strong>Important:</strong> Please submit your cancellation request as early as possible to ensure it can be processed.</p>
              </div>
            </div>
          </AnimatedSection>

          <AnimatedSection className="sds_legal-section">
            <h2 className="title is-3 sds_legal-section-title">2. Non-Cancellable Items</h2>
            <div className="content">
              <p>HDT Solutions does not accept cancellation requests for perishable items like flowers, eatables etc. However, refund/replacement can be made if the customer establishes that the quality of product delivered is not good.</p>
            </div>
          </AnimatedSection>

          <AnimatedSection className="sds_legal-section">
            <h2 className="title is-3 sds_legal-section-title">3. Damaged or Defective Items</h2>
            <div className="content">
              <p>In case of receipt of damaged or defective items please report the same to our Customer Service team. The request will, however, be entertained once the merchant has checked and determined the same at his own end.</p>
              <ul>
                <li>Report damaged items within <strong>2 days of receipt</strong></li>
                <li>Provide photos or evidence of the damage</li>
                <li>Wait for merchant verification</li>
                <li>Refund/replacement will be processed upon confirmation</li>
              </ul>
            </div>
          </AnimatedSection>

          <AnimatedSection className="sds_legal-section">
            <h2 className="title is-3 sds_legal-section-title">4. Product Not as Expected</h2>
            <div className="content">
              <p>In case you feel that the product received is not as shown on the site or as per your expectations, you must bring it to the notice of our customer service within <strong>2 days of receiving the product</strong>. The Customer Service Team after looking into your complaint will take an appropriate decision.</p>
            </div>
          </AnimatedSection>

          <AnimatedSection className="sds_legal-section">
            <h2 className="title is-3 sds_legal-section-title">5. Warranty Claims</h2>
            <div className="content">
              <p>In case of complaints regarding products that come with a warranty from manufacturers, please refer the issue to them directly. We will assist in facilitating the warranty claim process.</p>
            </div>
          </AnimatedSection>

          <AnimatedSection className="sds_legal-section">
            <h2 className="title is-3 sds_legal-section-title">6. Refund Processing</h2>
            <div className="content">
              <p>In case of any Refunds approved by HDT Solutions, it will take <strong>3-5 business days</strong> for the refund to be processed to the end customer.</p>
              <div className="notification is-warning is-light mt-4">
                <p><strong>Note:</strong> The time for the refund to reflect in your account may vary depending on your bank or payment provider.</p>
              </div>
            </div>
          </AnimatedSection>

          <AnimatedSection className="sds_legal-contact">
            <h2 className="title is-3 sds_legal-section-title">Contact Us</h2>
            <div className="content">
              <p>For cancellation requests or refund inquiries, please contact us at:</p>
              <div className="box sds_legal-contact-box">
                <p><strong>HDT Solutions</strong></p>
                <p>📧 Email: <a href="mailto:support@hdtsolution.com">support@hdtsolution.com</a></p>
                <p>📱 Phone: <a href="tel:+91 9553896536">{InfoService?.mobile_no_1}</a></p>
                <p>🌐 Website: <a href="https://hdtsecure.com/">hdtsecure.com</a></p>
              </div>
            </div>
          </AnimatedSection>
        </motion.div>
      </div>
    </div>
  );
};
export default CancellationPolicy;